import '../../../res/stylesheets/style.scss';
import {registerBrick, Config, CloudObject, Auth} from 'olympe';
import {ReactBrick, useProperty} from '@olympeio/core';
import React, {useEffect, useRef, useState} from 'react';
import {PREditor} from './pr_editor/PREditor.jsx';
import {ListOfPREditorsHeader} from './ListOfPREditorsHeader.jsx';
import CircularProgress from '@mui/material/CircularProgress';
import {PurchaseRequest} from '../models/PurchaseRequest';
import {createPRStore} from './pr_editor/utils/createStore';
import {map, startWith, switchMap} from 'rxjs/operators';
import AppController, {AppStates} from '../controllers/AppController';
import UserController from '../controllers/UserController';
import {combineLatest, EMPTY, of} from 'rxjs';
import {
    getEPRIdFromTagLambda, massCopyLambda,
    massDeleteLambda,
    massSubmitLambda,
    sendEmailLambda,
} from '../utils/functionSignatures';
import {ListOfPREditorsPagination} from './ListOfPREditorsPagination.jsx';
import {MassSubmitSuccessPopup} from './pr_editor/dialogs/MassSubmitSuccessPopup.jsx';
import {getMailContentPRMassSubmitted} from '../utils/email';
import {Button} from '@mui/material';
import CustomPopup from './dialogs/CustomPopup.jsx';
import {InvalidPSLMaterialIdPopup} from './dialogs/InvalidPSLMaterialIdPopup.jsx';
import {MassCopySuccessPopup} from './pr_editor/dialogs/MassCopySuccessPopup.jsx';
import {ErrorPopup} from './pr_editor/dialogs/ErrorPopup.jsx';
import {LAMBDA_NAMES} from '../utils/constants';
import PurchaseRequestDM from '../models/cloudobjects/PurchaseRequestDM';

export const requestStores = new Map();
export let useApplicationStore;

const sortKey = {
    CREATION_DATE: 'tagOlympe',
    SUBMISSION_DATE: PurchaseRequestDM.DB_COLUMNS.submissionDate,
};

export const sortDirection = {
    DESCENDING: 'desc',
    ASCENDING: 'asc',
};

/**
 * Brick that display a list of purchase requests
 */
export default class ListOfPREditors extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            AppController.getAppRefreshPRsListSubject(),
            $.observe('Application Store'),
        ]).pipe(map(() => []));
    }

    /**
     * @override
     */
    static getReactComponent($) {
        AppController.resetState();
        useApplicationStore = $.get('Application Store');

        // ITSD-79: stores should be recreated when the context is destroyed
        // TODO: a refactoring of the stores lifecycle would be even better
        $.onDestroy(() => requestStores.clear());

        /**
         * Check which tag have been modified
         * @param {[]} purchaseRequests
         * @return {{addOrDelete: string, tags: string[]}}
         */
        const findNewTag = (purchaseRequests) => {
            const numberOfNewPRs = purchaseRequests.filter((pr) => !requestStores.has(pr.tag));

            if (numberOfNewPRs.length === 0) { // If there is no new PR, we are adding a line item in an existing PR (or we are deleting a PR)
                if (purchaseRequests.length < requestStores.size) {// if we are deleting a PR
                    const existingTags = Array.from(requestStores.keys());
                    const deletedTag = existingTags.filter((pr) => !purchaseRequests.map((e) => e.tag).includes(pr.tag))[0];
                    return {
                        addOrDelete: '-',
                        tags: [deletedTag],
                    };
                } else { // If not, we are modifying a PR
                    const existingPRNbOfLineItems = new Map;
                    requestStores.forEach((value, key) => {
                        existingPRNbOfLineItems.set(key, value.getState().materials.length);
                    });
                    return {
                        addOrDelete: '+',
                        tags: [purchaseRequests.find((pr) => pr.pr.materials.length !== existingPRNbOfLineItems.get(pr.tag))?.tag],
                    };
                }
            } else if (numberOfNewPRs.length === 1) { // If there is one new PR, we are adding a new PR
                return {
                    addOrDelete: '+',
                    tags: [purchaseRequests.find((pr) => !requestStores.has(pr.tag)).tag],
                };
            } else { // If there are more than one new PR, this is the first render
                return {
                    addOrDelete: '+',
                    tags: purchaseRequests.map((pr) => pr.tag),
                };
            }
        };

        return () => {
            const prIdRef = useRef('');
            const prTagRef = useRef('');
            const [page, setPage] = useState(0);
            const [prCount, setPRCount] = useState(0);
            const [rowsPerPage, setRowsPerPage] = useState(5);
            const currentTab = $.get('Current tab');
            const [displayedRequests, setDisplayedRequests] = useState([]);
            const [selectPRsMode, setSelectPRsMode] = useState(false);
            const [selectedPRs, setSelectedPRs] = useState([]);
            const isAnyPRSelected = selectedPRs.length > 0;
            const [failedMassSubmittedPRs, setFailedMassSubmittedPRs] = useState([]);
            const [successfulMassCopiedPRs, setSuccessfulMassCopiedPRs] = useState([]);
            const [successfulMassSubmittedPRs, setSuccessfulMassSubmittedPRs] = useState([]);
            const [sbApprovalNeededMassSubmittedPRs, setSbApprovalNeededMassSubmittedPRs] = useState([]);
            const [toRequesterMassSubmittedPRs, setToRequesterMassSubmittedPRs] = useState([]);
            const [massSubmitLoading, setMassSubmitLoading] = useState(false);
            const [massDeleteLoading, setMassDeleteLoading] = useState(false);
            const [massCopyLoading, setMassCopyLoading] = useState(false);
            const [showMassSubmitPopup, setShowMassSubmitPopup] = useState(false);
            const [showMassCopyPopup, setShowMassCopyPopup] = useState(false);
            const [showMassDeletePopup, setShowMassDeletePopup] = useState(false);
            const [sortConfig, setSortConfig] = useState({key: sortKey.CREATION_DATE, direction: sortDirection.DESCENDING});
            const [showInvalidPSLMaterialIdPopup, setShowInvalidPSLMaterialIdPopup] = useState(false);
            const [invalidPSLMaterialIDMaterials, setInvalidPSLMaterialIDMaterials] = useState([]);
            const [invalidPSLMaterialIDAction, setInvalidPSLMaterialIDAction] = useState('');
            const takePerPage = $.get('Rows Per Page');
            const isLoading = useProperty($, 'Is Loading', false);
            const usersCheckingAPr = useProperty($, 'Users checking a PR', true);
            const failedPRs = useProperty($, 'Failed PRs', true);
            const massSubmit = $.get(LAMBDA_NAMES.MASS_SUBMIT_PRS);
            const massDelete = $.get(LAMBDA_NAMES.MASS_DELETE_PRS);
            const massCopy = $.get(LAMBDA_NAMES.MASS_COPY_PRS);
            const sendEmail = $.get(LAMBDA_NAMES.SEND_EMAIL);
            const EPRIdFromTag = $.get(LAMBDA_NAMES.GET_EPR_ID_FROM_TAG);
            const [errorMessage, setErrorMessage] = useState('');
            const [errorTitle, setErrorTitle] = useState('');
            const [showErrorPopup, setShowErrorPopup] = useState(false);

            // General subscription use effect
            useEffect(() => {
                const rawRequestsSub = combineLatest($.observe('Purchase Requests', true), $.observe('Refresh', true)).pipe(switchMap((newRawRequests) => {
                    return combineLatest(newRawRequests.length > 0 ? newRawRequests
                        ?.map((request) => of(request)) : EMPTY.pipe(startWith(0))).pipe(map(() => {
                        return newRawRequests;
                    }));
                })).subscribe(async ([newRawRequests, _]) => {
                    if (AppController.getCurrentAppState() !== AppStates.EDITING) {
                        // Check if URL has prId query string, and expand the specific PR automatically.
                        const params = new URLSearchParams(window.location.search);
                        const prId = params.get('prId');

                        if (prId) {
                            const eprIdRsp = await getEPRIdFromTagLambda($, EPRIdFromTag, prId);
                            prIdRef.current = eprIdRsp[0];
                            prTagRef.current = prId;
                        }

                        const purchaseRequests = newRawRequests?.map((r) => ({pr: new PurchaseRequest(r.json), tag: r.tag, total: r.total}));

                        const count = parseInt(newRawRequests[0]?.total || 0);
                        $.set('Number of PR', newRawRequests[0]?.total);
                        setPRCount( count);

                        // Find which PR has been created, modified or deleted, to update only this one
                        const newTags = findNewTag(purchaseRequests);

                        if (newTags.tags.length === 1) {
                            if (newTags.addOrDelete === '+' && !!newTags.tags[0]) {
                                const newPR = purchaseRequests.find((pr) => pr.tag === newTags.tags[0]);
                                const useRequestStore = createPRStore(newPR.pr, $);
                                requestStores.set(newPR.tag, useRequestStore);
                            }
                        } else if (newTags.tags.length > 1) {
                            purchaseRequests?.forEach((purchaseRequest) => {
                                const pr = purchaseRequest.pr;
                                const useRequestStore = createPRStore(pr, $);
                                requestStores.set(purchaseRequest.tag, useRequestStore);
                            });
                        }

                        // The PR list is automatically refreshed if one of the following condition is true
                        const modifiedPRs = purchaseRequests
                            .filter((pr) => ((JSON.stringify(pr.pr.spender)) !== JSON.stringify(requestStores.get(pr.tag)?.getState().spender)) ||
                                ((JSON.stringify(pr.pr.requester)) !== JSON.stringify(requestStores.get(pr.tag)?.getState().requester)) ||
                                (pr.pr.attachmentsInfo.mainAttachments.length !== requestStores.get(pr.tag)?.getState().mainAttachments.length) ||
                                (pr.pr.attachmentsInfo.competitiveBiddingJustificationAttachments.length !== requestStores.get(pr.tag)?.getState().competitiveBiddingJustificationAttachments.length) ||
                                ((pr.pr.status) !== requestStores.get(pr.tag)?.getState().status) ||
                                (pr.pr.materials.length !== requestStores.get(pr.tag)?.getState().materials.length),
                            );

                        // We re-create the store of modified PR
                        modifiedPRs?.forEach((modifiedPr) => {
                            const useRequestStore = createPRStore(modifiedPr.pr, $);
                            requestStores.set(modifiedPr.tag, useRequestStore);
                        });
                        setDisplayedRequests(purchaseRequests);
                    }
                });
                return () => rawRequestsSub.unsubscribe();
            }, []);

            useEffect(() => {
                if (failedPRs && failedPRs.length > 0) {
                    setFailedMassSubmittedPRs(failedPRs);
                }
                setSelectPRsMode(false);
            }, [failedPRs]);

            // the case when going through the page and the length of the result of filters less than the skip value
            useEffect(() => {
                if (displayedRequests.length === 0 && page > 0) {
                    setPage(0);
                    $.set('Skip', 0);
                }
            }, [displayedRequests.length, page]);

            // This useEffect resets the sorting to default values when tabs are changed
            useEffect(() => {
                $.set('Sort Key', sortKey.CREATION_DATE);
                $.set('Sort Direction', sortDirection.DESCENDING);
                setSortConfig({key: sortKey.CREATION_DATE, direction: sortDirection.DESCENDING});
                setSelectedPRs([]);
                setFailedMassSubmittedPRs([]);
                setSelectPRsMode(false);
            }, [currentTab]);

            const handleSort = (key, direction) => {
                $.set('Sort Key', key);
                $.set('Sort Direction', direction);
                setSortConfig({key, direction});
            };

            const removePrIdFromRef = () => {
                prIdRef.current = '';
                prTagRef.current = '';
            };

            const handleChangePage = (event, newPage) => {
                setPage(newPage);
                $.set('Skip', takePerPage * newPage);
            };

            const handleChangeRowsPerPage = (event) => {
                setRowsPerPage(+event.target.value);
                $.set('Rows Per Page', +event.target.value);
                setPage(0);
                $.set('Skip', 0);
            };

            const handleCloseErrorPopup = () => {
                setErrorMessage('');
                setErrorTitle('');
                setShowErrorPopup(false);
            };

            const handleMassCopy = () => {
                if (selectedPRs.length > 100) {
                    setErrorMessage('The Mass Copy feature has a limitation of 100 requests. Currently, more than 100 requests have been selected. Please reduce the number of selected requests and try again.');
                    setErrorTitle('Selection limit exceeded');
                    setShowErrorPopup(true);
                    return;
                }

                setMassCopyLoading(true);
                setFailedMassSubmittedPRs([]);
                const currentUserObject = CloudObject.get(Auth.getCurrentUser());
                const currentUser = localStorage.getItem('Current_Sap_User');

                massCopyLambda($, massCopy, selectedPRs, currentUser, currentUserObject)
                    .then((outputs) => {
                        const result = outputs[0];
                        const invalidMaterials = [];

                        const failedPRs = result.map((r) => {
                            if (r.errorDescriptor) {
                                if (r.errorDescriptor.errorTitle === 'Invalid PSL Material ID!') {
                                    invalidMaterials.push(...r.errorDescriptor.invalidMaterials);
                                }
                                return ({
                                    prTag: r.prTag,
                                    errorTitle: r.errorDescriptor.errorTitle,
                                    errorMessages: r.errorDescriptor.errorMessages,
                                });
                            }
                        }).filter((item) => item);
                        if (failedPRs.length > 0) {
                            setFailedMassSubmittedPRs(failedPRs);
                        }
                        if (invalidMaterials.length > 0) {
                            setInvalidPSLMaterialIDMaterials(invalidMaterials);
                            setInvalidPSLMaterialIDAction('copied');
                        }

                        const successfulPRs = result.map((r) => {
                            if (r.newPRID) {
                                return ({
                                    EPRID: r.newPRID,
                                    originalEPRID: r.originalEPRID,
                                });
                            }
                        }).filter((item) => item);
                        if (successfulPRs.length > 0) {
                            setSuccessfulMassCopiedPRs(successfulPRs);
                        }

                        if (invalidMaterials.length > 0) {
                            setShowInvalidPSLMaterialIdPopup(true);
                        } else if (successfulPRs.length > 0) {
                            setShowMassCopyPopup(true);
                        }

                        setMassCopyLoading(false);
                        setSelectedPRs([]);
                        setSelectPRsMode(false);
                    })
                    .catch((e) => {
                        console.error(e);

                        const failedPRs = selectedPRs.map((tag) => ({
                            prTag: tag,
                            errorTitle: 'Mass copy failed',
                            errorMessages: ['Something went wrong with mass copy process. Please try again.'],
                        }));
                        setSelectedPRs([]);
                        setFailedMassSubmittedPRs(failedPRs);
                        setMassCopyLoading(false);
                        setSelectPRsMode(false);
                    });
            };

            const handleMassDelete = () => {
                if (selectedPRs.length > 100) {
                    setErrorMessage('The Mass Delete feature has a limitation of 100 requests. Currently, more than 100 requests have been selected. Please reduce the number of selected requests and try again.');
                    setErrorTitle('Selection limit exceeded');
                    setShowErrorPopup(true);
                    return;
                }

                setShowMassDeletePopup(true);
            };

            const confirmMassDelete = () => {
                setShowMassDeletePopup(false);
                setMassDeleteLoading(true);

                massDeleteLambda($, massDelete, selectedPRs, UserController.getCurrentUserName('CAADAPAAM'))
                    .then((outputs) => {
                        setSelectedPRs([]);
                        setMassDeleteLoading(false);
                        setSelectPRsMode(false);
                    })
                    .catch((e) => {
                        console.error(e);

                        const failedPRs = selectedPRs.map((tag) => ({
                            prTag: tag,
                            errorTitle: 'Mass delete failed',
                            errorMessages: ['Something went wrong with mass deletion process. Please try again.'],
                        }));
                        setSelectedPRs([]);
                        setFailedMassSubmittedPRs(failedPRs);
                        setMassDeleteLoading(false);
                        setSelectPRsMode(false);
                    });
            };

            const massDeletePopupContent =
                selectedPRs.length > 1 ?
                    `the ${selectedPRs.length} selected requests` :
                    'the selected request';

            const handleMassSubmit = () => {
                if (selectedPRs.length > 100) {
                    setErrorMessage('The Mass Submit feature has a limitation of 100 requests. Currently, more than 100 requests have been selected. Please reduce the number of selected requests and try again.');
                    setErrorTitle('Selection limit exceeded');
                    setShowErrorPopup(true);
                    return;
                }

                setMassSubmitLoading(true);
                setFailedMassSubmittedPRs([]);
                const currentUserObject = CloudObject.get(Auth.getCurrentUser());

                massSubmitLambda($, massSubmit, selectedPRs, UserController.getCurrentUserName('CAADAPAAM'), currentUserObject)
                    .then((outputs) => {
                        const result = outputs[0]; // There's only one output in this lambda
                        const invalidMaterials = [];

                        const failedPRs = result.map((r) => {
                            if (r.errorDescriptor) {
                                if (r.errorDescriptor.nameOfBrick === 'Mass Submit Get PR Info') {
                                    if (r.errorDescriptor.errorTitle === 'Invalid PSL Material ID!') {
                                        invalidMaterials.push(...r.errorDescriptor.invalidMaterials);
                                    }
                                    return ({
                                        prTag: r.prTag,
                                        errorTitle: r.errorDescriptor.errorTitle,
                                        errorMessages: r.errorDescriptor.errorMessages,
                                    });
                                } else if (r.errorDescriptor.nameOfBrick === 'Read Submit PR API response') {
                                    return ({
                                        prTag: r.prTag,
                                        errorTitle: 'SAP API response error:',
                                        errorMessages: [r.errorDescriptor.description],
                                    });
                                }
                            }
                        }).filter((item) => item);
                        if (failedPRs.length > 0) {
                            setFailedMassSubmittedPRs(failedPRs);
                        }
                        if (invalidMaterials.length > 0) {
                            setInvalidPSLMaterialIDMaterials(invalidMaterials);
                            setInvalidPSLMaterialIDAction('submitted');
                        }

                        const successfulPRs = result.map((r) => {
                            if (r.sAPPRNumber) {
                                const prJson = JSON.parse(r.prJson);

                                if (prJson.EPRId) {
                                    return ({
                                        EPRID: prJson.EPRId,
                                        SAPPRNumber: r.sAPPRNumber,
                                        prJson: prJson,
                                    });
                                }
                            }
                        }).filter((item) => item);
                        if (successfulPRs.length > 0) {
                            setSuccessfulMassSubmittedPRs(successfulPRs);

                            const prsByRequester = successfulPRs.reduce((acc, pr) => {
                                const email = pr.prJson.requester?.email;
                                if (email) {
                                    if (!acc[email]) {
                                        acc[email] = {
                                            prs: [],
                                            spenders: new Set(),
                                        };
                                    }
                                    acc[email].prs.push(pr);
                                    if (pr.prJson.spender?.email) {
                                        acc[email].spenders.add(pr.prJson.spender.email);
                                    }
                                }
                                return acc;
                            }, {});

                            Object.keys(prsByRequester).forEach((email) => {
                                try {
                                    const env = Config.getParameter('ENV');

                                    const {prs, spenders} = prsByRequester[email];
                                    const prJson = prs[0].prJson;

                                    const ccMailAddress = Array.from(spenders).join(',');
                                    const mailContent = getMailContentPRMassSubmitted(prJson.requester, prs);

                                    sendEmailLambda($, sendEmail, email, ccMailAddress, '', `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PRs submitted`, mailContent, []);
                                } catch (e) {
                                    console.error('Error sending email:', e);
                                }
                            });
                        }

                        const sbApprovalNeededPRs = result.map((r) => {
                            if (r.sBNeeded) {
                                const prJson = JSON.parse(r.prJson);

                                if (prJson.EPRId) {
                                    return ({
                                        EPRID: prJson.EPRId,
                                        prJson: prJson,
                                        reasonsForApproval: r.reasonsForApproval,
                                        sBApprovers: r.sBApprovers,
                                        userIDToCallSAPAPIs: r.userIDToCallSAPAPIs,
                                        prTag: r.prTag,
                                    });
                                }
                            }
                        }).filter((item) => item);
                        if (sbApprovalNeededPRs.length > 0) {
                            setSbApprovalNeededMassSubmittedPRs(sbApprovalNeededPRs);
                        }

                        const submitToRequesterPrs = result.map((r) => {
                            if (r.submittedToRequester && !r.errorDescriptor) {
                                const prJson = JSON.parse(r.prJson);

                                if (prJson.EPRId) {
                                    return ({
                                        EPRID: prJson.EPRId,
                                        prJson: prJson,
                                    });
                                }
                            }
                        }).filter((item) => item);
                        if (submitToRequesterPrs.length > 0) {
                            setToRequesterMassSubmittedPRs(submitToRequesterPrs);
                        }


                        if (invalidMaterials.length > 0) {
                            setShowInvalidPSLMaterialIdPopup(true);
                        } else if (successfulPRs.length > 0 || sbApprovalNeededPRs.length > 0 || submitToRequesterPrs.length > 0) {
                            setShowMassSubmitPopup(true);
                        }

                        setMassSubmitLoading(false);
                        setSelectedPRs([]);
                        setSelectPRsMode(false);
                    })
                    .catch((e) => {
                        console.error(e);

                        const failedPRs = selectedPRs.map((tag) => ({
                            prTag: tag,
                            errorTitle: 'Mass submit failed',
                            errorMessages: ['Something went wrong with mass submission process. Please try again.'],
                        }));
                        setSelectedPRs([]);
                        setFailedMassSubmittedPRs(failedPRs);
                        setMassSubmitLoading(false);
                        setSelectPRsMode(false);
                    });
            };

            const handleOnCloseInvalidPSLMaterialIdPopup = () => {
                setShowInvalidPSLMaterialIdPopup(false);
                setInvalidPSLMaterialIDMaterials([]);

                if (successfulMassSubmittedPRs.length > 0 || sbApprovalNeededMassSubmittedPRs.length > 0 || toRequesterMassSubmittedPRs.length > 0) {
                    setShowMassSubmitPopup(true);
                } else if (successfulMassCopiedPRs.length > 0) {
                    setShowMassCopyPopup(true);
                }
            };

            return (
                <>
                    <div className={'list-of-pr'}>
                        {isLoading && <div className='list-of-pr-loader'>
                            <div className='list-of-pr-loader-inner'>
                                <div className='list-of-pr-loader-inner-text'>Fetching your requests...</div>
                                <CircularProgress color='secondary' thickness={4} size={50}/>
                            </div>
                        </div>}
                        <ListOfPREditorsHeader
                            context={$}
                            currentTab={currentTab}
                            handleSort={handleSort}
                            sortKey={sortKey}
                            sortDirection={sortDirection}
                            sortConfig={sortConfig}
                            displayedRequests={displayedRequests}
                            selectedPRs={selectedPRs}
                            setSelectedPRs={setSelectedPRs}
                            setSelectPRsMode={setSelectPRsMode}
                            failedMassSubmittedPRs={failedMassSubmittedPRs}
                            setFailedMassSubmittedPRs={setFailedMassSubmittedPRs}
                            massSubmitLoading={massSubmitLoading}
                            massDeleteLoading={massDeleteLoading}
                        />
                        <div className={'list-of-pr-editor-list'}>
                            {displayedRequests?.map((pr, rowIndex) => {
                                return (
                                    <div key={'purchaseRequest' + pr.tag} className={'list-of-pr-editor-item'}>
                                        <PREditor
                                            rowIndex={rowIndex}
                                            prTag={pr.tag}
                                            context={$}
                                            prIdFromQueryParam={prTagRef?.current}
                                            removePrIdFromRef={removePrIdFromRef}
                                            currentTab={currentTab}
                                            usersCheckingThePr={usersCheckingAPr?.get(pr.tag) || []}
                                            selectedPRs={selectedPRs}
                                            setSelectedPRs={setSelectedPRs}
                                            selectPRsMode={selectPRsMode}
                                            setSelectPRsMode={setSelectPRsMode}
                                            failedMassSubmittedPRs={failedMassSubmittedPRs}
                                            setFailedMassSubmittedPRs={setFailedMassSubmittedPRs}
                                            isAnyPRSelected={isAnyPRSelected}
                                            handleMassSubmit={handleMassSubmit}
                                            massSubmitLoading={massSubmitLoading}
                                            massDeleteLoading={massDeleteLoading}
                                            handleMassDelete={handleMassDelete}
                                            massCopyLoading={massCopyLoading}
                                            handleMassCopy={handleMassCopy}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <ListOfPREditorsPagination
                            page={page}
                            count={prCount}
                            rowsPerPage={rowsPerPage}
                            rowsPerPageOptions={[2, 5, 10, 20, 50]}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                        <MassSubmitSuccessPopup
                            context={$}
                            open={showMassSubmitPopup}
                            setOpen={setShowMassSubmitPopup}
                            prs={successfulMassSubmittedPRs}
                            prsSubmittedToRequester={toRequesterMassSubmittedPRs}
                            prsSBApprovalNeeded={sbApprovalNeededMassSubmittedPRs}
                            setSbApprovalNeededMassSubmittedPRs={setSbApprovalNeededMassSubmittedPRs}
                            failedMassSubmittedPRs={failedMassSubmittedPRs}
                        />
                        <MassCopySuccessPopup
                            context={$}
                            open={showMassCopyPopup}
                            setOpen={setShowMassCopyPopup}
                            prs={successfulMassCopiedPRs}
                            failedMassSubmittedPRs={failedMassSubmittedPRs}
                        />
                        <CustomPopup
                            title="Delete requests?"
                            content={`Are you sure you want to delete ${massDeletePopupContent}?`}
                            buttons={[
                                <Button
                                    key="cancel"
                                    onClick={() => setShowMassDeletePopup(false)}
                                    autoFocus
                                    variant="text"
                                    sx={{
                                        color: '#999999',
                                    }}
                                >
                                    Cancel
                                </Button>,
                                <Button
                                    key="delete"
                                    onClick={confirmMassDelete}
                                    disabled={massDeleteLoading}
                                    variant="text"
                                    sx={{
                                        color: '#999999',
                                    }}
                                >
                                    Delete
                                </Button>,
                            ]}
                            open={showMassDeletePopup}
                            handleClose={() => setShowMassDeletePopup(false)}
                            showErrorIcon={false}
                        />
                        <InvalidPSLMaterialIdPopup
                            open={showInvalidPSLMaterialIdPopup}
                            materials={invalidPSLMaterialIDMaterials}
                            typeOfAction={invalidPSLMaterialIDAction}
                            multipleRequests={true}
                            onClose={handleOnCloseInvalidPSLMaterialIdPopup}
                        />
                        {showErrorPopup && (
                            <ErrorPopup
                                onClose={handleCloseErrorPopup}
                                open={true}
                                title={errorTitle}
                                errorMessages={[errorMessage]}
                            />
                        )}
                    </div>
                </>
            );
        };
    }
}

registerBrick('017c4a50760732d99616', ListOfPREditors);
